import React, { useCallback } from "react";
import { useAppData } from "../../../../common/hooks/useAppData";
import { FormToolbar } from "../../../common/form/FormToolbar";
import {
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  FormDataConsumer,
  useTranslate,
  required,
} from "react-admin";
import { GenericDateOrTimePicker } from "../../../common/form/GenericDateOrTimePicker";
import { DatePicker, TimePicker } from "@material-ui/pickers";
import { isNumber } from "lodash";
import { parseISO } from "date-fns";
import { AddStatusDto } from "../../../../common/data/service-proxies";
import { useRouteMatch } from "react-router-dom";
import { CustomResourceRouteParams } from "../../../../common/types";

interface Props {
  timeZone?: string | null;
  record?: AddStatusDto;
}

export const DriverLogStatusForm = ({ timeZone, ...props }: Props) => {
  const countriesAndStates = useAppData((x) => x.countries) ?? [];
  const countries = countriesAndStates.map((x) => ({ id: x.id, name: x.name }));
  const appDataDutyStatuses = useAppData((x) => x.dutyStatuses) ?? [];
  const dutyStatuses = appDataDutyStatuses.map((x) => ({
    name: `appData.dutyCycles.${x}`,
    id: x,
  }));
  const route = useRouteMatch<CustomResourceRouteParams>();
  const translate = useTranslate();
  const checkAllowedRange = useCallback(
    async (value?: number | string, values?: any) => {
      if (!value) {
        return undefined;
      }

      if (!props.record?.driverId || !props.record?.date) {
        return undefined;
      }

      let dateToCheck: Date;
      if (!isNumber(value)) {
        dateToCheck = parseISO(value);
      } else {
        dateToCheck = new Date(value);
      }

      const currentDate = isNumber(route.params["date"])
        ? new Date(route.params["date"])
        : parseISO(route.params["date"]);
      const dateTimeValue = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
        dateToCheck.getHours(),
        dateToCheck.getMinutes(),
        dateToCheck.getSeconds(),
        dateToCheck.getMilliseconds()
      );

      if (dateTimeValue > new Date()) {
        return translate("resources.common.labels.futureTimeNotAllowed");
      }
    },
    [translate, props.record, route.params]
  );

  return (
    <div className="form-create-edit-drawer">
      <SimpleForm variant="outlined" toolbar={<FormToolbar />} {...props}>
        <GenericDateOrTimePicker
          PickerComponent={DatePicker}
          source="date"
          timeZone={timeZone}
          disabled
        />
        <GenericDateOrTimePicker
          PickerComponent={TimePicker}
          timeZone={timeZone}
          source="time"
          options={{
            views: ["hours", "minutes", "seconds"],
            format: "KK:mm:ss aa",
          }}
          validate={[required(), checkAllowedRange]}
        />
        <SelectInput
          source="dutyStatus"
          choices={dutyStatuses}
          validate={required()}
        />
        <TextInput source="location" validate={required()} />
        <NumberInput source="odometer" validate={required()} />
        <SelectInput
          source="country"
          choices={countries}
          translate="no"
          validate={required()}
        />
        <FormDataConsumer>
          {({ scopedFormData, formData, getSource, ...rest }) => (
            <SelectInput
              source="state"
              translate="no"
              choices={
                countriesAndStates.find((x) => x.id === formData["country"])
                  ?.states ?? []
              }
              {...rest}
              validate={required()}
            />
          )}
        </FormDataConsumer>
        <TextInput source="note" />
      </SimpleForm>
    </div>
  );
};
